/** @prettier */

/**
 * アップロードダイアログ
 */

import {
  Dialog,
  DialogTitle,
  DialogContentText,
  DialogContent,
  DialogActions,
  Box,
  Stack,
} from '@mui/material';
import {useState} from 'react';
import {Storage} from 'aws-amplify';
import {useTranslation} from 'react-i18next';

//MTH component import
import APIErrorMessage from '../../component/APIErrorMessage';
import ImageUploadButton, {
  CSVUploadButton,
} from '../../component/FileUploadButton';
import {PrimaryButton, SecondaryButton} from '../../component/BasicButton';
//Style import
import '../../css/common/common.css';

import {limit, file} from '../../define';
import {addTranslation} from '../../functions/addTranslation';

/**
 * ロゴ画像アップロードダイアログ
 * @param props props
 * @returns JSX
 */
const LogoUploadDialog = (props: {
  /** ロゴの名前 */
  logoName: string;
  /** ダイアログ表示フラグ */
  isOpenDialog: boolean;
  /** ダイアログ表示フラグ設定関数 */
  setIsOpenDialog: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const trans = useTranslation().t; //翻訳関数
  Storage.configure({customPrefix: {private: file.LOGO_S3_KEY_PREFIX}});

  const [imageFile, setImageFile] = useState<File | undefined>(undefined); //アップロードする画像データ
  const [imgSizeError, setImgSizeError] = useState(false); //レイアウト画像エラーフラグ
  const [uploadBtnDisabled, setUploadBtnDisabled] = useState(true); //UPLOADボタン非表示フラグ
  const [isOpenErrorDialog, setIsOpenErrorDialog] = useState(false); //APIエラー時のエラーダイアログ表示フラグ
  const [apiWait, setAPIWait] = useState(false); //API応答待ちフラグ

  /**
   * UPLOADボタン押下時処理
   */
  const uploadImage = () => {
    //画像がない場合
    if (imageFile === undefined) {
      return;
    }

    setUploadBtnDisabled(true);
    setAPIWait(true);

    //アップロード
    Storage.put(props.logoName, imageFile, {
      level: 'private',
      contentType: 'image/png',
    })
      .then(response => {
        handleClose();
      })
      .catch(() => {
        setIsOpenErrorDialog(true);
      })
      .finally(() => {
        setAPIWait(false);
        window.location.reload();
      });
  };
  /**
   * CANCELボタン押下時処理
   */
  const handleClose = () => {
    setImgSizeError(false);
    setImageFile(undefined);
    setUploadBtnDisabled(true);
    props.setIsOpenDialog(false);
  };

  return (
    <>
      <Dialog
        open={props.isOpenDialog}
        onClose={handleClose}
        aria-labelledby="logo-upload-title">
        <DialogTitle id="logo-upload-title">
          {trans('WebsiteSettingUploadDialog.logo.title')}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3}>
            <DialogContentText className="accountSetting__dialog__content__text">
              {trans('WebsiteSettingUploadDialog.logo.msg', {
                size: limit.UPLOAD_FILE_SIZE_MAX,
              })}
            </DialogContentText>
            {/* ファイル選択ボタン */}
            <ImageUploadButton
              setImageFunc={imageFile => {
                setImageFile(imageFile);
                setUploadBtnDisabled(false);
              }}
              setIsError={setImgSizeError}
              setUploadBtnDisabled={setUploadBtnDisabled}
            />
            {imgSizeError && (
              <Box className="accountSetting__dialog__content__text--error">
                {trans(
                  'WebsiteSettingUploadDialog.logo.errMsg.imageSizeExceed',
                  {size: limit.UPLOAD_FILE_SIZE_MAX}
                )}
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <SecondaryButton
            disabled={apiWait}
            onClick={handleClose}
            buttonText={trans('WebsiteSettingUploadDialog.buttonTxt.cancel')}
          />
          <PrimaryButton
            onClick={uploadImage}
            buttonText={trans('WebsiteSettingUploadDialog.buttonTxt.ok')}
            disabled={uploadBtnDisabled || imgSizeError}
            apiWait={apiWait}
          />
        </DialogActions>
      </Dialog>
      <APIErrorMessage
        open={isOpenErrorDialog}
        setOpen={setIsOpenErrorDialog}
      />
    </>
  );
};

/**
 * 言語ファイルアップロードダイアログ
 * @param props props
 * @returns JSX
 */
const LanguageUploadDialog = (props: {
  /** ファイル名 */
  fileName: string;
  /** ダイアログ表示フラグ */
  isOpenDialog: boolean;
  /** ダイアログ表示フラグ設定関数 */
  setIsOpenDialog: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const trans = useTranslation().t; //翻訳関数
  Storage.configure({customPrefix: {private: file.LANGUAGE_S3_KEY_PREFIX}});

  const [language, setLanguage] = useState<File | undefined>(undefined); //アップロードする言語ファイル
  const [uploadBtnDisabled, setUploadBtnDisabled] = useState(true); //UPLOADボタン非表示フラグ
  const [isOpenErrorDialog, setIsOpenErrorDialog] = useState(false); //APIエラー時のエラーダイアログ表示フラグ
  const [apiWait, setAPIWait] = useState(false); //API応答待ちフラグ

  /**
   * UPLOADボタン押下時処理
   */
  const uploadFile = () => {
    //言語ファイルがない場合
    if (language === undefined) {
      return;
    }
    //規定のフォーマット以外無効化
    if (language.type !== 'text/csv') {
      alert(trans('WebsiteSettingUploadDialog.alertMsg.invalidFile'));
      return;
    }

    setUploadBtnDisabled(true);
    setAPIWait(true);

    //アップロード
    Storage.put(props.fileName, language, {
      level: 'private',
      contentType: 'text/csv',
    })
      .then(response => {
        //ファイルを読み込む
        const reader = new FileReader();
        reader.readAsText(language);
        reader.onload = () => {
          addTranslation(reader.result as string); //言語データを翻訳リソースに追加する
        };
        handleClose();
      })
      .catch(() => {
        setIsOpenErrorDialog(true);
      })
      .finally(() => {
        setAPIWait(false);
        window.location.reload();
      });
  };
  /**
   * CANCELボタン押下時処理
   */
  const handleClose = () => {
    setLanguage(undefined);
    setUploadBtnDisabled(true);
    props.setIsOpenDialog(false);
  };

  return (
    <>
      <Dialog
        open={props.isOpenDialog}
        onClose={handleClose}
        aria-labelledby="language-file-upload-title">
        <DialogTitle id="language-file-upload-title">
          {trans('WebsiteSettingUploadDialog.language.title')}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3}>
            <DialogContentText className="accountSetting__dialog__content__text">
              {trans('WebsiteSettingUploadDialog.language.msg')}
            </DialogContentText>
            {/* ファイル選択ボタン */}
            <CSVUploadButton
              id="mv-userLangSelect"
              setCSVFunc={csv => {
                setLanguage(csv);
                setUploadBtnDisabled(false);
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <SecondaryButton
            disabled={apiWait}
            onClick={handleClose}
            buttonText={trans('WebsiteSettingUploadDialog.buttonTxt.cancel')}
          />
          <PrimaryButton
            onClick={uploadFile}
            buttonText={trans('WebsiteSettingUploadDialog.buttonTxt.ok')}
            disabled={uploadBtnDisabled}
            apiWait={apiWait}
          />
        </DialogActions>
      </Dialog>
      <APIErrorMessage
        open={isOpenErrorDialog}
        setOpen={setIsOpenErrorDialog}
      />
    </>
  );
};

export {LogoUploadDialog, LanguageUploadDialog};
